import { useEffect } from "react";
import WebsiteShowcaseSection from "../sections/WebsiteShowcaseSection";
import { BRAND_CONFIGS, type BrandType } from "../../../config/brandConfig";
import {
  Star,
  BadgeCheck,
  Info,
  Target,
  AlertTriangle,
  Route,
  Lightbulb,
  FileCheck,
  CheckCircle,
  CheckCircle2,
  Smile,
  TrendingUp,
  Banknote,
  Wand2,
  Flag,
  ShieldCheck,
  Send,
} from "lucide-react";
import { SiUpwork } from "react-icons/si";

const MinimalTemplate = ({ proposal }: any) => {
useEffect(() => {
  const link = document.createElement("link");
  link.href =
    "https://fonts.googleapis.com/css2?family=Public+Sans:wght@300;400;500;600;700&display=swap";
  link.rel = "stylesheet";
  document.head.appendChild(link);

  return () => {
    document.head.removeChild(link);
  };
}, []);

  const stats = proposal.stats || [
    { value: "100%", label: "JOB SUCCESS" },
    { value: "100%", label: "CLIENT SATISFACTION" },
    { value: "100%", label: "OUTCOME SUCCESS" },
    { value: "100%", label: "REVENUE GROWTH" },
  ];

  const getStatIcon = (label: string) => {
    const l = label?.toLowerCase() || "";
    if (l.includes("job") && l.includes("success")) return CheckCircle2;
    if (l.includes("satisfaction") || l.includes("client")) return Smile;
    if (l.includes("outcome")) return TrendingUp;
    if (l.includes("revenue") || l.includes("earning")) return Banknote;
    return Star;
  };

  const sectionIconMap: Record<string, any> = {
    "simple-text": Info,
    "feature-grid": Flag,
    "dark-grid": AlertTriangle,
    steps: Route,
    "text-with-points": Lightbulb,
    "testimonial-grid": Star,
    "portfolio-grid": FileCheck,
    "call-to-action": ShieldCheck,
  };

  const scrollTo = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const sorted = (proposal.sections || [])
    .filter((s: any) => !s.hidden)
    .sort((a: any, b: any) => a.order - b.order);

  const paired = new Set<number>();

  const renderSection = (section: any, index: number) => {
    if (paired.has(index)) return null;

    if (section.type === "feature-grid") {
      const next = sorted[index + 1];
      if (next && next.type === "dark-grid") {
        paired.add(index + 1);
        return (
          <div key={index} id={`section-${section.id}`} className="mb-24">
            <div className="grid md:grid-cols-2 gap-16">

              <div>
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-2 text-[#001e00]">
                  <Flag size={18} className="text-[#14a800]" />
                  {section.title}
                </h3>
                <ul className="space-y-5">
                  {section.content?.items?.map((item: any, i: number) => (
                    <li key={i} className="flex gap-3 items-start">
                      <CheckCircle
                        size={20}
                        className="mt-0.5 flex-shrink-0"
                        color="#14a800"
                        fill="#14a800"
                        strokeWidth={0}
                      />
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {item.description || item.title}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>

              <div id={`section-${next.id}`}>
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-2 text-[#001e00]">
                  <AlertTriangle size={18} className="text-[#14a800]" />
                  {next.title}
                </h3>
                <ul className="space-y-5">
                  {next.content?.items?.map((item: any, i: number) => (
                    <li key={i} className="flex gap-3 items-start">
                      <Info
                        size={20}
                        className="text-slate-400 mt-0.5 flex-shrink-0"
                      />
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {item.description || item.title}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
      }

      return (
        <section key={index} id={`section-${section.id}`} className="mb-24">
          <h3 className="text-2xl font-bold mb-6 flex items-center gap-2 text-[#001e00]">
            <Flag size={18} className="text-[#14a800]" />
            {section.title}
          </h3>
          <div className="grid md:grid-cols-2 gap-5">
            {section.content?.items?.map((item: any, i: number) => (
              <div key={i} className="flex gap-3 items-start">
                <CheckCircle size={18} className="text-[#14a800] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-[#001e00] text-sm">{item.title}</p>
                  <p className="text-slate-500 text-sm mt-1 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      );
    }

    if (section.type === "dark-grid") {
      const highlights = section.content?.highlights || [];
      return (
        <section key={index} id={`section-${section.id}`} className="mb-24">
          <div className="bg-[#001e00] rounded-3xl p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-80 h-80 bg-[#14a800]/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl pointer-events-none" />
            <div className="relative z-10">
              <h3 className="text-2xl font-bold text-white mb-5">
                {section.title}
              </h3>
              {section.content?.text && (
                <p className="text-white/70 text-lg leading-relaxed max-w-3xl mb-10">
                  {section.content.text}
                </p>
              )}
              {!section.content?.text &&
                section.content?.items?.map((item: any, i: number) => (
                  <p key={i} className="text-white/70 text-lg leading-relaxed mb-3">
                    {item.description || item.title}
                  </p>
                ))}
              {highlights.length > 0 && (
                <div
                  className="grid gap-8 mt-8"
                  style={{ gridTemplateColumns: `repeat(${highlights.length}, 1fr)` }}
                >
                  {highlights.map((h: any, i: number) => (
                    <div key={i}>
                      <p className="text-[#14a800] text-3xl font-bold">{h.value}</p>
                      <p className="text-xs uppercase text-white/40 mt-1 tracking-wider">{h.label}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      );
    }

    if (section.type === "simple-text") {
      return (
        <section key={index} id={`section-${section.id}`} className="mb-24">
          <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
            <span className="w-8 h-1 bg-[#14a800] rounded-full flex-shrink-0" />
            {section.title}
          </h3>
          <p className="text-lg text-slate-600 leading-loose">{section.content?.text}</p>
        </section>
      );
    }

    if (section.type === "steps") {
      return (
        <section key={index} id={`section-${section.id}`} className="mb-24">
          <h3 className="text-2xl font-bold mb-12 flex items-center gap-3">
            <Route size={20} className="text-[#14a800] flex-shrink-0" />
            {section.title}
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {section.content?.steps?.map((step: any, i: number) => (
              <div key={i} className="p-8 border border-slate-100 rounded-2xl hover:border-[#14a800]/40 transition-colors">
                <span className="text-[#14a800] text-xs font-bold mb-4 block tracking-widest">
                  STEP 0{i + 1}
                </span>
                <div className="w-10 h-10 rounded-lg bg-[#14a800]/10 flex items-center justify-center mb-4">
                  <span className="text-[#14a800] font-bold text-sm">{i + 1}</span>
                </div>
                <h4 className="text-lg font-bold mb-3 text-[#001e00]">{step.title}</h4>
                <p className="text-slate-500 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </section>
      );
    }

    if (section.type === "text-with-points") {
      const pts = section.content?.points || [];
      // Only render stats that were actually authored — never hardcode
      // web-dev metrics (SEO/load-speed) onto e.g. a logo-design proposal.
      const solStats = section.content?.stats || section.content?.highlights || [];
      return (
        <section key={index} id={`section-${section.id}`} className="mb-24">
          <div className="bg-[#002800] rounded-3xl p-12 md:p-14">
            <h3 className="text-2xl font-bold text-white mb-5">{section.title}</h3>

            {section.content?.text && (
              <p className="text-white/70 text-lg leading-relaxed max-w-3xl mb-8 whitespace-pre-line">
                {section.content.text}
              </p>
            )}

            {pts.length > 0 && (
              <ul className="space-y-3 mb-8 max-w-3xl">
                {pts.map((pt: any, i: number) => (
                  <li key={i} className="flex gap-3 items-start">
                    <CheckCircle size={20} className="mt-0.5 flex-shrink-0" color="#14a800" fill="#14a800" strokeWidth={0} />
                    <p className="text-white/80 text-base leading-relaxed">
                      {typeof pt === "string" ? pt : pt.text || pt.title}
                    </p>
                  </li>
                ))}
              </ul>
            )}

            {section.content?.highlight && (
              <div className="rounded-2xl border border-dashed border-[#14a800]/40 bg-[#14a800]/10 p-6 max-w-3xl">
                <p className="text-white font-semibold text-lg leading-relaxed whitespace-pre-line">
                  {section.content.highlight}
                </p>
              </div>
            )}

            {solStats.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-10">
                {solStats.map((s: any, i: number) => (
                  <div key={i}>
                    <p className="text-[#14a800] text-3xl font-bold">{s.value}</p>
                    <p className="text-xs uppercase text-white/50 mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      );
    }

    if (section.type === "testimonial-grid") {
      return (
        <section key={index} id={`section-${section.id}`} className="mb-24">
          <h3 className="text-2xl font-bold mb-12 flex items-center gap-3">
            <Star size={20} className="text-[#14a800] flex-shrink-0" />
            {section.title}
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {section.content?.items?.map((item: any, i: number) => {
              const initials = item.name
                ?.split(" ")
                .map((n: string) => n[0])
                .join("")
                .substring(0, 2)
                .toUpperCase();
              return (
                <div key={i} className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm">
                  <div className="flex gap-0.5 mb-4">
                    {Array(5).fill(0).map((_, si) => (
                      <Star key={si} size={14} fill="#14a800" strokeWidth={0} className="text-[#14a800]" />
                    ))}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed italic mb-6">"{item.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#14a800]/10 flex items-center justify-center text-xs font-bold text-[#14a800]">
                      {initials}
                    </div>
                    <div>
                      <p className="font-bold text-[#001e00] text-sm">{item.name}</p>
                      <p className="text-xs text-slate-400">{item.position}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      );
    }

    if (section.type === "website-showcase") {
      return <WebsiteShowcaseSection key={section.id} section={section} primaryColor={proposal.customization?.primaryColor} />;
    }

    if (section.type === "portfolio-grid") {
      return (
        <section key={index} id={`section-${section.id}`} className="mb-24">
          <h3 className="text-2xl font-bold mb-3">{section.title}</h3>
          {section.content?.subtitle && (
            <p className="text-slate-500 mb-10">{section.content.subtitle}</p>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {section.content?.projects?.map((project: any, i: number) => (
              <div key={i} className="group relative overflow-hidden rounded-2xl aspect-[4/3] bg-slate-100">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-contain bg-slate-100 group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#001e00]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                  <p className="text-white font-bold text-sm">{project.title}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      );
    }

    if (section.type === "call-to-action") {
      return (
        <section key={index} id="section-cta" className="border-t border-slate-100 pt-20 text-center mb-12">
          <h3 className="text-4xl font-bold mb-6 tracking-tight text-[#001e00]">
            {section.title}
          </h3>
          <p className="text-xl text-slate-500 mb-10 max-w-2xl mx-auto leading-relaxed">
            {section.content?.text}
          </p>
          <a
            href={section.content?.buttonLink || proposal.hireLink || BRAND_CONFIGS[proposal.brand as BrandType]?.upworkProfile || "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#14a800] text-white px-10 py-4 rounded-2xl font-bold text-lg hover:bg-[#14a800]/90 transition-all inline-flex items-center gap-3"
          >
            <Send size={18} />
            {section.content?.buttonText || `Hire ${proposal.fullName || "Me"}`}
          </a>
          <p className="mt-8 text-sm text-slate-400">
            Typical response time:{" "}
            <span className="text-[#14a800] font-medium">Under 4 hours</span>
          </p>
        </section>
      );
    }

    return null;
  };

  return (
    <div style={{ fontFamily: "'Public Sans', sans-serif" }} className="bg-white text-[#001e00] min-h-screen">

      <div className="fixed left-4 top-1/2 -translate-y-1/2 z-50 hidden lg:flex flex-col gap-2 bg-white p-3 rounded-full shadow-xl border border-slate-100">
        {sorted.map((section: any, i: number) => {
          const Icon = sectionIconMap[section.type] || Info;
          const active = i === 0;
          return (
            <button
              key={i}
              onClick={() => scrollTo(`section-${section.id}`)}
              title={section.title}
              className="group relative flex items-center justify-center w-8 h-8 rounded-full transition-all hover:bg-[#14a800]/10"
              style={{ background: active ? "#14a800" : "transparent" }}
            >
              <Icon size={15} color={active ? "#fff" : "#9ca3af"} />
              <span className="absolute left-11 whitespace-nowrap bg-[#001e00] text-white text-xs px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
                {section.title}
              </span>
            </button>
          );
        })}
      </div>

      <div className="fixed right-0 top-1/2 -translate-y-1/2 z-50 hidden lg:block">
        <button
          onClick={() => {
            const url = proposal.hireLink || BRAND_CONFIGS[proposal.brand as BrandType]?.upworkProfile;
            if (url) window.open(url, "_blank", "noopener");
            else scrollTo("section-cta");
          }}
          className="bg-[#14a800] text-white w-16 py-6 rounded-l-2xl shadow-2xl flex flex-col items-center gap-2.5 font-bold hover:bg-[#14a800]/90 transition-all"
        >
          <ShieldCheck size={20} strokeWidth={2} />
          <span className="text-[22px] uppercase tracking-widest font-bold"
            style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
            HIRE ME
          </span>
        </button>
      </div>

<div className="fixed bottom-4 right-4 z-50 lg:hidden">
  <button
    onClick={() => {
      const url = proposal.hireLink || BRAND_CONFIGS[proposal.brand as BrandType]?.upworkProfile;
      if (url) window.open(url, "_blank", "noopener");
      else scrollTo("section-cta");
    }}
    className="px-4 bg-[#14a800] text-white py-4 rounded-2xl shadow-2xl flex items-center justify-center gap-3 font-bold text-lg"
  >
    <SiUpwork size={22} />

  </button>
</div>
      <main className="max-w-5xl mx-auto px-6 py-12 lg:py-20">

        <header className="mb-20">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 border-b border-slate-100 pb-12">
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 rounded-full bg-slate-200 overflow-hidden ring-4 ring-[#14a800]/20 flex-shrink-0">
                <img
                  src={proposal.profileImage?.trim() || "/upwork.png"}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-[#001e00]">
                  {proposal.fullName || "Irfan Baig"}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  <span className="flex items-center gap-1 text-[#14a800] text-sm font-semibold">
                    <Star size={13} fill="#14a800" strokeWidth={0} /> Top Rated
                  </span>
                  <span className="w-1 h-1 bg-slate-300 rounded-full" />
                  <span className="flex items-center gap-1 text-slate-500 text-sm">
                    <BadgeCheck size={14} className="text-[#14a800]" />
                    Upwork Verified
                  </span>
                </div>
              </div>
            </div>
            <div className="md:text-right max-w-md">
              <h2 className="text-2xl font-bold text-[#14a800] leading-snug">
                {proposal.tagline || "CMS Developer | WordPress, Shopify, Wix & Squarespace Expert"}
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
            {stats.map((item: any, i: number) => {
              const num = parseInt(item.value);
              const pct = isNaN(num) ? 80 : Math.min(num, 100);
              const Icon = getStatIcon(item.label);
              return (
                <div key={i} className="bg-[#14a800]/5 p-5 rounded-xl border border-[#14a800]/10 flex flex-col items-center text-center">
                  <Icon size={22} className="text-[#14a800] mb-2.5" strokeWidth={2} />
                  <p className="text-[10px] uppercase tracking-tighter text-slate-500 font-bold mb-1">
                    {item.label}
                  </p>
                  <p className="text-2xl font-bold text-[#001e00]">{item.value}</p>
                  <div className="w-full bg-slate-200 h-1.5 rounded-full mt-3 overflow-hidden">
                    <div
                      className="bg-[#14a800] h-full rounded-full transition-all duration-700"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </header>

        {proposal.title && (
          <div className="mb-16 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#001e00] leading-tight">
              {proposal.title}
            </h2>
            <div className="w-16 h-1.5 bg-[#14a800] rounded-full mx-auto mt-4" />
          </div>
        )}

        {sorted.map((section: any, index: number) => renderSection(section, index))}

      </main>

    </div>
  );
};

export default MinimalTemplate;
